import { useGrantSession } from './useGrantSession'
import { readGrantMirror } from '../lib/grantMirror'
import type { GrantPoc } from '../types/grant'

export interface CanvasserCampaign {
  id: string
  label: string
  poc: GrantPoc | null
  nextShiftDate: string | null
  nextShiftStartTime: string | null
}

/**
 * [Canvasser redesign Phase 1 / spec D1] The canvasser's ONE campaign. A canvasser grant is always
 * exactly one campaign, so there is no picker and no `?campaignId=` in any URL — this replaced the
 * old URL-param active-campaign hook (since deleted).
 *
 * Source of truth is the live `/api/grant/me` identity. When it isn't available (offline cold
 * start: RequireGrant lets a network-errored session through on the mirror), fall back to the
 * localStorage mirror, which has no next shift. A LIVE identity with no campaign (an attendee, or
 * a deleted campaign) is authoritative — a stale mirror must not resurrect a campaign.
 */
export function useGrantCampaign(): CanvasserCampaign | null {
  const { identity } = useGrantSession()
  if (identity) {
    if (!identity.campaign) return null
    return {
      id: identity.campaign.id,
      label: identity.campaign.label,
      poc: identity.poc ?? null,
      nextShiftDate: identity.campaign.nextShiftDate ?? null,
      nextShiftStartTime: identity.campaign.nextShiftStartTime ?? null,
    }
  }
  const mirror = readGrantMirror()
  if (!mirror?.campaign) return null
  return {
    id: mirror.campaign.id,
    label: mirror.campaign.label,
    poc: mirror.poc ?? null,
    nextShiftDate: null,
    nextShiftStartTime: null,
  }
}
